import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Badge } from './ui/badge';
import { Plus, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthenticatedAxios, handleAuthError } from '../utils/api';

const TokenManagement = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newToken, setNewToken] = useState({
    token_symbol: '',
    token_address: '',
    enabled: true,
    min_swap_amount_ton: 0.1,
    max_swap_amount_ton: 1.0,
    priority: 1,
  });

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get('/api/warmup/tokens');
      if (response.data.success) {
        setTokens(response.data.tokens);
      }
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to fetch tokens');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToken = async () => {
    try {
      const api = getAuthenticatedAxios();
      await api.post('/api/warmup/tokens', newToken);
      toast.success('Token added');
      setShowAddDialog(false);
      setNewToken({
        token_symbol: '',
        token_address: '',
        enabled: true,
        min_swap_amount_ton: 0.1,
        max_swap_amount_ton: 1.0,
        priority: 1,
      });
      fetchTokens();
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to add token');
      }
    }
  };

  const handleDeleteToken = async (id) => {
    if (!window.confirm('Are you sure you want to delete this token?')) {
      return;
    }
    try {
      const api = getAuthenticatedAxios();
      await api.delete(`/api/warmup/tokens/${id}`);
      toast.success('Token deleted');
      fetchTokens();
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to delete token');
      }
    }
  };

  const handleToggleEnabled = async (id, enabled) => {
    try {
      const api = getAuthenticatedAxios();
      const token = tokens.find(t => t.id === id);
      await api.put(`/api/warmup/tokens/${id}`, {
        ...token,
        enabled: !enabled,
      });
      toast.success('Token updated');
      fetchTokens();
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to update token');
      }
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Token Management</CardTitle>
            <CardDescription>Manage tokens for warmup operations</CardDescription>
          </div>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Token
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Symbol</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Min Amount (TON)</TableHead>
              <TableHead>Max Amount (TON)</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tokens.map((token) => (
              <TableRow key={token.id}>
                <TableCell>{token.token_symbol}</TableCell>
                <TableCell className="font-mono text-xs">
                  {token.token_address.slice(0, 20)}...
                </TableCell>
                <TableCell>{token.min_swap_amount_ton}</TableCell>
                <TableCell>{token.max_swap_amount_ton}</TableCell>
                <TableCell>{token.priority}</TableCell>
                <TableCell>
                  <Switch
                    checked={token.enabled}
                    onCheckedChange={() => handleToggleEnabled(token.id, token.enabled)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteToken(token.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {showAddDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Add Token</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Token Symbol</Label>
                  <Input
                    value={newToken.token_symbol}
                    onChange={(e) => setNewToken({ ...newToken, token_symbol: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Token Address</Label>
                  <Input
                    value={newToken.token_address}
                    onChange={(e) => setNewToken({ ...newToken, token_address: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Min Amount (TON)</Label>
                    <Input
                      type="number"
                      value={newToken.min_swap_amount_ton}
                      onChange={(e) => setNewToken({ ...newToken, min_swap_amount_ton: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Max Amount (TON)</Label>
                    <Input
                      type="number"
                      value={newToken.max_swap_amount_ton}
                      onChange={(e) => setNewToken({ ...newToken, max_swap_amount_ton: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Priority (1-10, lower is higher priority)</Label>
                  <Input
                    type="number"
                    value={newToken.priority}
                    onChange={(e) => setNewToken({ ...newToken, priority: parseInt(e.target.value) })}
                    min="1"
                    max="10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddToken}>Add</Button>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TokenManagement;

